/**
 * Continuous body amounts: the tuck amount and the arm geometry (shoulders,
 * reach direction with grip assist, hand point, reach radius). The shoulders
 * and the reach direction rotate with the attitude about the center of mass,
 * so the flight position decides where the hands are relative to the mass.
 * Everything downstream reads these amounts, never the buttons.
 */

import { capsuleHeight, tuckFloor } from "./collision";
import { cos, moveToward, sin, sqrt } from "./fastmath";
import type { SimState } from "./state";
import type { CompiledTuning } from "./tuning";
import { DT, type InputFrame } from "./types";
import type { World } from "./world";

/**
 * Ease tuckAmount toward the held target, never below the ceiling floor. On the ground the floor is
 * the room above the feet (standing up under a pipe); in the air, where the mass holds still, it is
 * the room above the mass for the head (an extension under a ceiling).
 */
export function updateTuck(world: World, s: SimState, input: InputFrame, t: CompiledTuning): void {
  const held = s.barId >= 0 || s.ledgeSolid >= 0;
  const floor = s.grounded === 1 || held ? tuckFloor(world, s, t) : airTuckFloor(world, s, t);
  s.tuckFloor = floor;
  // While the hands are planted the legs come up beside the box whatever the button says; a press armed
  // for a vault does not crouch.
  let target = s.planted !== 0 ? t.vaultTuck : input.tuck && s.vaultArmBox < 0 ? 1 : 0;
  if (floor > target) target = floor;
  const rate = target > s.tuck ? t.tuckInRate : t.tuckOutRate;
  s.tuck = moveToward(s.tuck, target, rate * DT);
}

/**
 * Smallest tuck whose head fits under the ceiling above the mass, with the mass held still (the air).
 * Near a floor the extension cannot lower the feet through it, so within `tuckComDrop` of one the body
 * must also fit standing on that floor under the same ceiling: a jump out of a slide under a pipe
 * keeps the slide's crouch until it is clear of the pipe, instead of growing into it (the head pushed
 * down under the pipe drove the feet into the floor, and the collision then threw the body to the
 * floor slab's far edge).
 */
export function airTuckFloor(world: World, s: SimState, t: CompiledTuning): number {
  const r = t.capsuleRadius;
  const com = s.y + comHeight(t, s.tuck);
  const room = world.roomAbove(s.x - r, s.x + r, com);
  // Head above the mass: capsuleHeight(tuck) - comHeight(tuck), falling by (crouchDrop - tuckComDrop) per tuck.
  const headStand = t.standHeight - comHeight(t, 0);
  let f = 0;
  if (room < headStand) {
    const shrink = t.crouchDrop - t.tuckComDrop;
    if (shrink > 1e-9) f = (headStand - room) / shrink;
  }
  const floorTop = world.probeDown(s.x - r, s.x + r, s.y, t.tuckComDrop);
  if (floorTop > Number.NEGATIVE_INFINITY) {
    const gap = world.roomAbove(s.x - r, s.x + r, floorTop);
    if (gap < t.standHeight) {
      const g = (t.standHeight - gap) / t.crouchDrop;
      if (g > f) f = g;
    }
  }
  return f < 0 ? 0 : f > 1 ? 1 : f;
}

/** Shoulder height above the feet for the current tuck. */
export function shoulderHeight(t: CompiledTuning, tuck: number): number {
  return capsuleHeight(t, tuck) - t.shoulderBelowTop;
}

/** Center of mass height above the feet: half the standing height, drawn toward the feet by the tuck. */
export function comHeight(t: CompiledTuning, tuck: number): number {
  return t.standHeight * 0.5 - t.tuckComDrop * tuck;
}

/** Pendulum rest length for the current tuck: straight arms plus the torso offset. */
export function restLength(t: CompiledTuning, tuck: number): number {
  return t.armLength + t.torsoOffset + (t.torsoOffsetTucked - t.torsoOffset) * tuck;
}

export function reachRadius(t: CompiledTuning, reach: number): number {
  return t.reachRadiusBase + t.reachRadiusPerReach * reach;
}

/**
 * Derive shoulders, reach direction, hand point, and reach radius. The
 * shoulders sit above the center of mass along the torso axis and the default
 * reach direction is `reachDirection` above forward in the body frame, both
 * rotated by `phi`. With grip assist on, the reach direction turns toward the
 * nearest bar inside the cone (in the body frame); the shoulders stay where
 * the attitude put them. While attached the hands sit on the bar.
 */
export function updateArms(world: World, s: SimState, t: CompiledTuning): void {
  s.armsOut = s.reach >= t.gripThreshold ? 1 : 0;
  s.reachRadius = reachRadius(t, s.reach);
  const com = comHeight(t, s.tuck);
  const above = shoulderHeight(t, s.tuck) - com;
  const sp = sin(s.phi);
  const cp = cos(s.phi);
  s.shoulderX = s.x + above * sp;
  s.shoulderY = s.y + com + above * cp;

  if (s.plantHands === 1 && s.plantBox >= 0) {
    // A vault: on the way up the hands reach for the box's near top edge; over the box they rest on
    // its top vaultHandAhead ahead of the mass (kept a little in from both edges). The drawn arms reach
    // them through IK; the point is where the hands are, not a length the sim enforces.
    const box = s.plantBox;
    const inset = t.capsuleRadius * 0.5;
    const x0 = world.solidX0[box]! + inset;
    const x1 = world.solidX1[box]! - inset;
    const near = s.facing > 0 ? x0 : x1;
    const ahead = s.x + s.facing * t.vaultHandAhead;
    const under = ahead < x0 ? x0 : ahead > x1 ? x1 : ahead;
    const targetX = s.planted === 1 ? near : under;
    // On the top, not in it: the fist's radius above the surface.
    const targetY = world.solidY1[box]! + t.handOnTop;
    const dx = targetX - s.shoulderX;
    const dy = targetY - s.shoulderY;
    const d = sqrt(dx * dx + dy * dy);
    if (d > 1e-9) {
      s.reachDirX = dx / d;
      s.reachDirY = dy / d;
    }
    s.handX = targetX;
    s.handY = targetY;
    s.assistBar = -1;
    return;
  }

  if (s.ledgeSolid >= 0) {
    // Hanging from a ledge: the hands hold its top edge, a fist's radius onto the top.
    const i = s.ledgeSolid;
    const cx = s.ledgeDir > 0 ? world.solidX0[i]! : world.solidX1[i]!;
    s.handX = cx + s.ledgeDir * t.handOnTop;
    s.handY = world.solidY1[i]! + t.handOnTop;
    const dx = s.handX - s.shoulderX;
    const dy = s.handY - s.shoulderY;
    const d = sqrt(dx * dx + dy * dy);
    if (d > 1e-9) {
      s.reachDirX = dx / d;
      s.reachDirY = dy / d;
    }
    s.assistBar = -1;
    return;
  }

  if (s.barId >= 0) {
    s.handX = world.barX[s.barId]!;
    s.handY = world.barY[s.barId]! + s.ladderLiftY;
    const dx = s.handX - s.shoulderX;
    const dy = s.handY - s.shoulderY;
    const d = sqrt(dx * dx + dy * dy);
    if (d > 1e-9) {
      s.reachDirX = dx / d;
      s.reachDirY = dy / d;
    }
    s.assistBar = -1;
    return;
  }

  // Default direction: up and forward in the body frame, rotated into the world by phi.
  const bx0 = s.facing * cos(t.reachDirRad);
  const by0 = sin(t.reachDirRad);
  let dirX = bx0 * cp + by0 * sp;
  let dirY = -bx0 * sp + by0 * cp;
  s.assistBar = -1;

  if (t.gripAssist > 0 && s.armsOut === 1) {
    let best = -1;
    let bestD2 = t.gripAssistRange * t.gripAssistRange;
    for (let i = 0; i < world.barCount; i++) {
      if (i === s.lastBarId && (s.lockout > 0 || s.leftRadius === 0)) continue;
      if (!world.barGrippable(i, s.ladderId, s.ladderRung)) continue;
      const bx = world.barX[i]! - s.shoulderX;
      const by = world.barY[i]! - s.shoulderY;
      const d2 = bx * bx + by * by;
      if (d2 > bestD2 || d2 < 1e-12) continue;
      const d = sqrt(d2);
      const dot = (bx * dirX + by * dirY) / d;
      if (dot < t.gripAssistCos) continue;
      bestD2 = d2;
      best = i;
    }
    if (best >= 0) {
      const bx = world.barX[best]! - s.shoulderX;
      const by = world.barY[best]! - s.shoulderY;
      const d = sqrt(bestD2);
      dirX = bx / d;
      dirY = by / d;
      s.assistBar = best;
    }
  }

  s.reachDirX = dirX;
  s.reachDirY = dirY;
  const ext = t.armLength * s.reach;
  s.handX = s.shoulderX + dirX * ext;
  s.handY = s.shoulderY + dirY * ext;
}
